import React, { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Button, Text } from '@fragments';
import { IconLibraryPhoto, IconSearch, IconUpload, IconUsersGroup, IconX } from '@tabler/icons-react';
import ImageGrid, { useImagePages } from '../ImageGrid';
import './styles.scss';

const ORIGINS = [{ key: 'upload', label: 'libraryUploads' }, { key: 'generated', label: 'libraryGenerated' }];
const SEARCH_DELAY_MS = 300;

// ←/→ (and Home/End) move between the chips of a row, as in a tab list.
const chipKeys = event => {
  const chips = [...event.currentTarget.querySelectorAll('[role="tab"]:not(:disabled)')];
  const at = chips.indexOf(document.activeElement);
  if (at < 0) return;
  const to = { ArrowRight: at + 1, ArrowLeft: at - 1, Home: 0, End: chips.length - 1 }[event.key];
  if (to === undefined) return;
  event.preventDefault();
  const chip = chips[(to + chips.length) % chips.length];
  chip.focus();
  chip.scrollIntoView?.({ block: 'nearest', inline: 'nearest' });
};

function Chips({ label, items, value, onChange }) {
  return <div className="art-library__chips" role="tablist" aria-label={label} onKeyDown={chipKeys}>
    {items.map(item => <Button key={item.key ?? 'all'} role="tab" aria-selected={value === item.key} tabIndex={value === item.key ? 0 : -1} size="small" variant="rounded"
      color={value === item.key ? 'inverse' : 'overlay'} preventLoading onClick={() => onChange(item.key)}>{item.name}</Button>)}
  </div>;
}

// The images a host offers (`images`, see the README), on one search over their
// descriptions: the creator's own, in two tabs — "Enviadas por você" (`origin: upload`)
// and "Geradas nas suas artes" (`origin: generated`) — then "Da comunidade"
// (`images.community`), the curated library by category. Community tiles show their
// thumbnail; a pick hands over the item itself (its full `url` is what goes in the art).
// With `onUpload(files) → images`, "Enviar foto" heads the creator's section and the new
// image joins its list first; `added` does the same for uploads made elsewhere.
export default function ImageLibrary({ source, selectedId, onPick, onUpload = null, added = null, disabled = false, columns = 3, pickLabel, tileAction, className = '' }) {
  const { t } = useTranslation('artEditor');
  const [query, setQuery] = useState('');
  const [q, setQ] = useState('');
  const [origin, setOrigin] = useState('upload');
  const [category, setCategory] = useState(null);
  const [categories, setCategories] = useState([]);
  const [upload, setUpload] = useState({ busy: false, failed: false });
  const input = useRef(null);
  const alive = useRef(true);
  useEffect(() => () => { alive.current = false; }, []);
  useEffect(() => {
    const timer = setTimeout(() => setQ(query.trim().slice(0, 100)), SEARCH_DELAY_MS);
    return () => clearTimeout(timer);
  }, [query]);

  // A new source (another profile's gallery) lists everything again.
  const generation = useRef({ source, value: 0 });
  if (generation.current.source !== source) generation.current = { source, value: generation.current.value + 1 };
  const sourceKey = generation.current.value;
  const community = source?.community || null;
  const mine = useImagePages(source?.list ? cursor => source.list({ cursor, origin, ...(q ? { q } : {}) }) : null, `${sourceKey}:${origin}:${q}`);
  const shared = useImagePages(community ? cursor => community.list({ cursor, ...(category ? { category } : {}), ...(q ? { q } : {}) }) : null,
    `${sourceKey}:${!!community}:${category}:${q}`);
  useEffect(() => {
    if (!community) { setCategories([]); return undefined; }
    let live = true;
    community.categories().then(list => live && setCategories((list || []).filter(item => item.image_count !== 0))).catch(() => live && setCategories([]));
    return () => { live = false; };
  }, [community]);

  // Uploads made elsewhere (the panel's own button) lead the uploads tab.
  const { prepend } = mine;
  useEffect(() => { if (added?.length && origin === 'upload') prepend(added); }, [added, origin, prepend]);

  const send = async files => {
    setUpload({ busy: true, failed: false });
    try {
      const images = (await onUpload(files)) || [];
      if (!alive.current) return;
      setUpload({ busy: false, failed: false });
      if (!images.length) return;
      if (origin === 'upload' && !q) prepend(images); else { setQuery(''); setQ(''); setOrigin('upload'); }
    } catch {
      if (alive.current) setUpload({ busy: false, failed: true });
    }
  };

  const searching = !!q;
  const mineEmpty = searching ? t('libraryNoResults') : t(origin === 'generated' ? 'libraryGeneratedEmpty' : 'galleryEmpty');
  return <div className={`art-library ${className}`}>
    <label className="art-search art-library__search">
      <IconSearch aria-hidden="true" />
      <input type="text" inputMode="search" enterKeyHint="search" value={query} maxLength={100} onChange={event => setQuery(event.target.value)}
        onKeyDown={event => { if (event.key === 'Escape' && query) { event.stopPropagation(); setQuery(''); } }} placeholder={t('librarySearch')} aria-label={t('librarySearch')} />
      {query && <Button size="micro" variant="text" color="overlay" preventLoading icon={<IconX />} aria-label={t('clearSearch')} onClick={() => setQuery('')} />}
    </label>
    <section className="art-library__section" aria-label={t('libraryMine')}>
      <div className="art-library__head">
        <Text.subheading contrast={6} left={<IconLibraryPhoto />}>{t('libraryMine')}</Text.subheading>
        {onUpload && <>
          <input ref={input} type="file" accept="image/png,image/jpeg,image/webp" hidden
            onChange={event => { const files = [...event.target.files]; event.target.value = ''; if (files.length) send(files.slice(0, 1)); }} />
          <Button size="small" variant="rounded" color="overlay" startIcon={<IconUpload />} loading={upload.busy} disabled={disabled || upload.busy}
            preventLoading onClick={() => input.current?.click()}>{t('uploadPhoto')}</Button>
        </>}
      </div>
      {upload.failed && <div className="art-inline-error" role="alert"><Text.caption contrast={8}>{t('libraryUploadFailed')}</Text.caption></div>}
      <Chips label={t('libraryMine')} value={origin} onChange={setOrigin} items={ORIGINS.map(item => ({ key: item.key, name: t(item.label) }))} />
      <div role="tabpanel" aria-label={t(ORIGINS.find(item => item.key === origin).label)}>
        <ImageGrid items={mine.items} status={mine.status} next={mine.next} onMore={mine.more} onRetry={() => mine.reload()} selectedId={selectedId}
          onPick={onPick} empty={mineEmpty} columns={columns} disabled={disabled} pickLabel={pickLabel} tileAction={tileAction} />
      </div>
    </section>
    {community && <section className="art-library__section" aria-label={t('libraryCommunity')}>
      <div className="art-library__head"><Text.subheading contrast={6} left={<IconUsersGroup />}>{t('libraryCommunity')}</Text.subheading></div>
      {categories.length > 0 && <Chips label={t('categories')} value={category} onChange={setCategory} items={[{ key: null, name: t('libraryAllCategories') }, ...categories.map(item => ({ key: item.key, name: item.name }))]} />}
      <div role={categories.length ? 'tabpanel' : undefined} aria-label={categories.length ? (categories.find(item => item.key === category)?.name || t('libraryAllCategories')) : undefined}>
        <ImageGrid items={shared.items} status={shared.status} next={shared.next} onMore={shared.more} onRetry={() => shared.reload()} selectedId={selectedId}
          onPick={onPick} empty={searching ? t('libraryNoResults') : t('communityEmpty')} error={t('communityError')} columns={columns} disabled={disabled}
          pickLabel={pickLabel} tileAction={tileAction} />
      </div>
    </section>}
  </div>;
}
